import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    TableCell,
    TableRow,
    Box,
    Typography,
    Checkbox,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RerunIcon from "../../assets/iconComponents/RerunIcon";
import ApproveAndRunIcon from "../../assets/iconComponents/ApproveAndRunIcon";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PageService from "../../services/PageService";
import TaskService from "../../services/TaskService";
import { formatDate } from "../../utils/formatDate";
import { TEST_STATUS_STYLES } from "../../theme/statusColors";

const TestCasesListItem = ({
    testCase,
    cycleContext = null,
    onPreviewTestCase = null,
    checkedTestCases,
    setTestCaseToEdit,
    setEditTestCaseOpen,
    handleCheckboxChange,
    handleOpenDeleteDialog,
    tabToggler,
    setTabToggler,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const isMenuOpen = Boolean(anchorEl);
    const [runTaskId, setRunTaskId] = useState(null);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const navigate = useNavigate();

    const statusStyles = TEST_STATUS_STYLES;
    const publicId = testCase?.public_id || testCase?.publicId || "-";
    const normalizedStatus = String(testCase?.status || "").toUpperCase();
    const isDraft = normalizedStatus === "DRAFT";
    const isRunning = normalizedStatus === "RUNNING";
    const canRunTestCase = [
        "DRAFT",
        "FAILED",
        "COMPLETED",
        "INITIAL_FAILURE",
    ].includes(normalizedStatus);

    const handleOpenTestCase = () => {
        if (anchorEl) return;
        if (typeof onPreviewTestCase === "function") {
            onPreviewTestCase(testCase);
            return;
        }
        navigate(`/test-case/${testCase.id}`);
    };

    const handleRunTestCase = async () => {
        try {
            const payload =
                cycleContext?.cycleId || testCase?.cycleId
                    ? {
                          originType: "cycle",
                          testCycleId:
                              cycleContext?.cycleId || testCase?.cycleId,
                          projectVersionId:
                              cycleContext?.versionId || testCase?.versionId,
                      }
                    : {};
            const res = await PageService.runTestCase(testCase?.id, payload);
            const taskId = res?.data?.taskId || res?.taskId;
            if (taskId) {
                setRunTaskId(taskId);
            }
            setTabToggler(!tabToggler);
        } catch (error) {
            console.log(error);
        }
    };

    const handleStopRun = async () => {
        if (!runTaskId) return;
        try {
            await TaskService.stopTaskById(runTaskId);
        } catch (error) {
            console.log(error);
        } finally {
            setRunTaskId(null);
        }
    };

    const handleEditTestCase = async () => {
        setTestCaseToEdit({
            id: testCase.id,
            name: testCase.name,
        });
        setEditTestCaseOpen(true);
    };

    return (
        <TableRow
            key={testCase.id}
            className="testCasesTableBodyRow"
            onClick={handleOpenTestCase}
        >
            <TableCell padding="checkbox" sx={{ width: 28, minWidth: 28 }}>
                <Checkbox
                    size="small"
                    checked={checkedTestCases.includes(testCase.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleCheckboxChange(testCase.id)}
                />
            </TableCell>
            <TableCell className="testCasesTableBodyCell testCaseId">
                {publicId}
            </TableCell>
            <TableCell className="testCasesTableBodyCell">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {publicId}
                    </Typography>
                    <Typography>{testCase.name}</Typography>
                </Box>
            </TableCell>
            <TableCell className="testCasesTableBodyCell">
                {formatDate(testCase.created_at)}
            </TableCell>
            <TableCell className="testCasesTableBodyCell">
                {testCase.regenerated_at
                    ? formatDate(testCase.regenerated_at)
                    : formatDate(testCase.updated_at)}
            </TableCell>
            <TableCell className="testCasesTableBodyCell">
                {testCase.created_by || "-"}
            </TableCell>
            <TableCell className="testCasesTableBodyCell">
                <Box className="testCasesTableBodyStatusContainer">
                    <Tooltip
                        title={
                            testCase.status === "INITIAL_FAILURE"
                                ? 'The test case failed on the first run, which indicates a potential issue in the positive flow. Negative scenarios were not generated automatically. You can review the test result, fix the issue and regenerate tests, or manually generate negative scenarios using the "Generate negative scenarios" button.'
                                : ""
                        }
                        disableHoverListener={
                            testCase.status !== "INITIAL_FAILURE"
                        }
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                },
                            },
                        }}
                    >
                        <Box
                            className="testCasesTableBodyStatus"
                            sx={{
                                ...statusStyles[testCase.status],
                                whiteSpace: "nowrap",
                                fontSize: "12px",
                                lineHeight: 1.2,
                                padding: "4px 10px",
                                maxWidth: "150px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {testCase.status === "RUNNING"
                                ? "In Progress..."
                                : testCase.status === "COMPLETED"
                                ? "Passed"
                                : testCase.status === "INITIAL_FAILURE"
                                ? "Initial failure"
                                : testCase.status.toLowerCase()}
                        </Box>
                    </Tooltip>
                </Box>
            </TableCell>
            <TableCell className="testCasesTableBodyCell testCasesActions">
                {isRunning ? (
                    <Tooltip
                        title="Stop"
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                },
                            },
                        }}
                    >
                        <IconButton
                            className="testCasesActionsIconContainer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleStopRun();
                            }}
                        >
                            <StopCircleOutlinedIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                ) : canRunTestCase ? (
                    <Tooltip
                        title={isDraft ? "Approve and Run" : "Re-Run"}
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                },
                            },
                        }}
                    >
                        <IconButton
                            className="testCasesActionsIconContainer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRunTestCase();
                            }}
                        >
                            {isDraft ? (
                                <ApproveAndRunIcon color="#2D4356" />
                            ) : (
                                <RerunIcon color="#2D4356" />
                            )}
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Box
                        className="testCasesActionsDisabledIconContainer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {isDraft ? (
                            <ApproveAndRunIcon color="#2D4356" />
                        ) : (
                            <RerunIcon color="#2D4356" />
                        )}
                    </Box>
                )}
                <IconButton
                    className="testCasesActionsIconContainer"
                    onClick={handleMenuOpen}
                    disabled={isRunning}
                >
                    <MoreVertIcon
                        color="primary"
                        sx={{
                            opacity: isRunning ? 0.3 : 1,
                        }}
                    />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClose();
                            handleEditTestCase();
                        }}
                    >
                        <EditOutlinedIcon sx={{ mr: 2 }} color="primary" />
                        Edit Test Case
                    </MenuItem>
                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClose();
                            handleOpenDeleteDialog(testCase?.id);
                        }}
                    >
                        <DeleteOutlineOutlinedIcon
                            sx={{ mr: 2 }}
                            color="error"
                        />
                        <Typography color="error">Delete Test Case</Typography>
                    </MenuItem>
                </Menu>
            </TableCell>
        </TableRow>
    );
};

export default TestCasesListItem;
